import type { CmsProject } from '../types/cms';
import { zonedParts } from './whatsappWorkingHours';
import type { WhatsappAutoReplySettings } from './whatsappWorkingHours';

export type AutoReplyLang = 'en' | 'gu' | 'hi';
export type LangText = Record<AutoReplyLang, string>;

export const AUTO_REPLY_LANGS: Array<{ key: AutoReplyLang; label: string }> = [
  { key: 'en', label: 'English' },
  { key: 'gu', label: 'ગુજરાતી' },
  { key: 'hi', label: 'हिंदी' },
];

export const AUTO_REPLY_MESSAGES_DOC = 'autoReplyMessages';
export const AUTO_REPLY_DEFAULTS_DOC = 'autoReplyDefaults';
export const AUTO_REPLY_VERSIONS_SUBCOLLECTION = 'versions';

/** WhatsApp limits: interactive body / caption 1024, reply button & list button 20. */
export const MESSAGE_MAX_CHARS = 1024;
export const BUTTON_MAX_CHARS = 20;
/** List row title and interactive footer limits. */
export const ROW_MAX_CHARS = 24;
export const FOOTER_MAX_CHARS = 60;
export const TRIGGER_PHRASE_MAX_CHARS = 80;
export const TRIGGER_PHRASES_MAX = 12;

/** These flows can be started by a customer's exact text as well as a button tap. */
export const TEXT_TRIGGER_FLOWS = ['brochure', 'siteVisit', 'callback', 'projectExplore'] as const;

/** Inbound texts (trimmed, case-insensitive) that opt a contact out of / back into automatic messages. */
export const OPT_OUT_KEYWORDS = ['stop', 'unsubscribe', 'stop messages', 'opt out', 'બંધ', 'बंद'];
export const OPT_IN_KEYWORDS = ['start', 'subscribe', 'શરૂ', 'शुरू'];

export interface PlaceholderDef {
  key: string;
  description: string;
}

export const PLACEHOLDERS: PlaceholderDef[] = [
  { key: 'name', description: "Customer's first name (falls back to \"there\")" },
  { key: 'project', description: 'Project name' },
  { key: 'location', description: 'Project location' },
  { key: 'config', description: 'Configurations, e.g. 2 & 3 BHK' },
  { key: 'price', description: 'Not used: prices are never shown on WhatsApp' },
  { key: 'possession', description: 'Ready possession / completion date' },
  { key: 'rera', description: 'RERA number' },
  { key: 'next', description: 'When the office opens next, e.g. 10:00 AM tomorrow' },
  { key: 'exec', description: 'Assigned sales executive (falls back to "our team")' },
  { key: 'slot', description: 'Site visit slot the customer picked' },
  { key: 'callTime', description: 'Call-back time the customer picked' },
];

export interface FlowButtonDef {
  id: string;
  defaults: LangText;
}

export interface FlowMessageDef {
  key: string;
  label: string;
  /** Placeholders that must stay in every language. */
  required: string[];
  /** Placeholders that make sense in this message (shown as insert chips). */
  placeholders: string[];
  defaults: LangText;
  buttons?: FlowButtonDef[];
  /** Opens a WhatsApp list; rows are filled by the CRM. */
  listButton?: FlowButtonDef & { rows: 'projects' | 'slots' };
  /** Extra list rows with editable titles (max 24 chars), e.g. a human hand-off. */
  fixedRows?: FlowButtonDef[];
  /** Sent as the header of the button message. */
  attachment?: 'brochure';
  /** A native WhatsApp location pin of the project site follows this message. */
  sendsLocation?: boolean;
  /** Shared footer: max 60 chars, may be empty. */
  role?: 'footer';
}

export interface FlowDef {
  key: string;
  title: string;
  startsWhen: string;
  messages: FlowMessageDef[];
  action?: string;
  end?: string;
}

const PROJECT_TOKENS = ['name', 'project', 'location', 'config', 'price', 'possession', 'rera'];

const BTN_BROCHURE: FlowButtonDef = { id: 'brochure', defaults: { en: '📄 Brochure', gu: '📄 બ્રોશર', hi: '📄 ब्रोशर' } };
const BTN_VISIT: FlowButtonDef = { id: 'visit', defaults: { en: '📍 Book site visit', gu: '📍 સાઇટ વિઝિટ બુક', hi: '📍 साइट विज़िट बुक' } };
const BTN_CALLBACK: FlowButtonDef = { id: 'callback', defaults: { en: '📞 Request a call', gu: '📞 કૉલ બેક માંગો', hi: '📞 कॉल बैक माँगें' } };

export const AUTO_REPLY_FLOWS: FlowDef[] = [
  {
    key: 'afterHours',
    title: 'After-hours reply',
    startsWhen:
      'Customer writes while the office is closed (outside working hours or on a holiday). Not for "ok", stickers or button taps. Once per 12 hours.',
    messages: [
      {
        key: 'main',
        label: 'After-hours reply',
        required: ['next'],
        placeholders: [...PROJECT_TOKENS, 'next'],
        defaults: {
          en: 'Hi {name} 👋\n\nThank you for messaging *Swarna Group*. 🌙 Our office is closed right now and opens again at *{next}*.\n\nYour message has reached us, and our team will reply as soon as we’re back.\n\nNo need to wait. Tap below to:\n📄 Get the project brochure\n📍 Book a site visit\n📞 Request a call back',
          gu: 'નમસ્તે {name} 👋\n\n*સ્વર્ણ ગ્રુપ* ને મેસેજ કરવા બદલ આભાર. 🌙 અત્યારે અમારી ઓફિસ બંધ છે અને *{next}* ફરી ખુલશે.\n\nતમારો મેસેજ અમને મળી ગયો છે, ઓફિસ ખુલતાં જ અમારી ટીમ તમને જવાબ આપશે.\n\nરાહ જોવાની જરૂર નથી. નીચે ટેપ કરો:\n📄 પ્રોજેક્ટ બ્રોશર મેળવો\n📍 સાઇટ વિઝિટ બુક કરો\n📞 કૉલ બેક માંગો',
          hi: 'नमस्ते {name} 👋\n\n*स्वर्ण ग्रुप* को मैसेज करने के लिए धन्यवाद। 🌙 अभी हमारा ऑफिस बंद है और *{next}* फिर खुलेगा।\n\nआपका मैसेज हमें मिल गया है, ऑफिस खुलते ही हमारी टीम आपको जवाब देगी।\n\nइंतज़ार की ज़रूरत नहीं। नीचे टैप करें:\n📄 प्रोजेक्ट ब्रोशर पाएँ\n📍 साइट विज़िट बुक करें\n📞 कॉल बैक माँगें',
        },
        buttons: [BTN_BROCHURE, BTN_VISIT, BTN_CALLBACK],
      },
    ],
    action: 'Saves an "after-hours" record. The chat stays unread for the team and shows in the overnight queue.',
    end: 'The chat goes to the Messages inbox for the team.',
  },
  {
    key: 'newContact',
    title: 'First reply (office hours)',
    startsWhen: 'A new number writes during office hours and we have never messaged them (e.g. tapped our ad). Instant, once. When the project is not known, the Unknown contact list goes instead.',
    messages: [
      {
        key: 'main',
        label: 'First reply',
        required: [],
        placeholders: [...PROJECT_TOKENS, 'exec'],
        defaults: {
          en: 'Hi {name} 👋\n\nThank you for your interest in *{project}* by *Swarna Group*. 🏡\n\n📍 {location}\n🏠 {config}\n🔑 {possession}\n\n*{exec}* will reply to you here shortly. Meanwhile, tap below to:\n📄 Get the brochure\n📍 Book a site visit\n📞 Request a call back',
          gu: 'નમસ્તે {name} 👋\n\n*સ્વર્ણ ગ્રુપ* ના *{project}* માં રસ બતાવવા બદલ આભાર. 🏡\n\n📍 {location}\n🏠 {config}\n🔑 {possession}\n\n*{exec}* થોડી જ વારમાં અહીં તમને જવાબ આપશે. ત્યાં સુધી નીચે ટેપ કરો:\n📄 બ્રોશર મેળવો\n📍 સાઇટ વિઝિટ બુક કરો\n📞 કૉલ બેક માંગો',
          hi: 'नमस्ते {name} 👋\n\n*स्वर्ण ग्रुप* के *{project}* में रुचि दिखाने के लिए धन्यवाद। 🏡\n\n📍 {location}\n🏠 {config}\n🔑 {possession}\n\n*{exec}* जल्द ही यहाँ आपको जवाब देंगे। तब तक नीचे टैप करें:\n📄 ब्रोशर पाएँ\n📍 साइट विज़िट बुक करें\n📞 कॉल बैक माँगें',
        },
        buttons: [BTN_BROCHURE, BTN_VISIT, BTN_CALLBACK],
      },
    ],
    action: 'Saves a "first reply" record. It never counts as a reply: the chat stays new until someone answers.',
    end: 'The chat stays with the assigned executive.',
  },
  {
    key: 'busyNote',
    title: 'Busy note',
    startsWhen: 'Office hours, and nobody has answered the customer within the wait time. Once per wait.',
    messages: [
      {
        key: 'main',
        label: 'Busy note',
        required: [],
        placeholders: ['name', 'project', 'exec'],
        defaults: {
          en: 'Hi {name} 👋\n\nThank you for messaging *Swarna Group*. We’re attending to other customers right now, and *{exec}* will reply to you here shortly.\n\nWhile you wait, tap below to:\n📄 Get the project brochure\n📍 Book a site visit\n📞 Request a call back',
          gu: 'નમસ્તે {name} 👋\n\n*સ્વર્ણ ગ્રુપ* ને મેસેજ કરવા બદલ આભાર. અત્યારે અમે અન્ય ગ્રાહકોને મદદ કરી રહ્યા છીએ, *{exec}* થોડી જ વારમાં અહીં તમને જવાબ આપશે.\n\nત્યાં સુધી નીચે ટેપ કરો:\n📄 પ્રોજેક્ટ બ્રોશર મેળવો\n📍 સાઇટ વિઝિટ બુક કરો\n📞 કૉલ બેક માંગો',
          hi: 'नमस्ते {name} 👋\n\n*स्वर्ण ग्रुप* को मैसेज करने के लिए धन्यवाद। अभी हम दूसरे ग्राहकों की मदद कर रहे हैं, *{exec}* जल्द ही यहाँ आपको जवाब देंगे।\n\nतब तक नीचे टैप करें:\n📄 प्रोजेक्ट ब्रोशर पाएँ\n📍 साइट विज़िट बुक करें\n📞 कॉल बैक माँगें',
        },
        buttons: [BTN_BROCHURE, BTN_VISIT, BTN_CALLBACK],
      },
    ],
    action: 'Saves a "busy note" record. It never counts as a reply: the chat stays new until someone answers.',
    end: 'The chat stays with the assigned executive.',
  },
  {
    key: 'brochure',
    title: 'Brochure',
    startsWhen: 'Customer taps "Brochure" on any auto-reply.',
    messages: [
      {
        key: 'main',
        label: 'Brochure caption',
        required: [],
        placeholders: PROJECT_TOKENS,
        defaults: {
          en: 'Here is the *{project}* brochure 📄\n{config}\nRERA: {rera}\n\nWould you like to see it in person?',
          gu: 'આ રહ્યું *{project}* નું બ્રોશર 📄\n{config}\nRERA: {rera}\n\nશું તમે રૂબરૂ જોવા માંગો છો?',
          hi: 'यह रहा *{project}* का ब्रोशर 📄\n{config}\nRERA: {rera}\n\nक्या आप इसे खुद देखना चाहेंगे?',
        },
        attachment: 'brochure',
        buttons: [BTN_VISIT, BTN_CALLBACK],
      },
    ],
    action: 'Sends the brochure PDF from the website CMS as the header of this message, with the buttons under it. If the project has no brochure, the text and buttons go without it.',
    end: 'Saves a "brochure sent" activity on the lead.',
  },
  {
    key: 'siteVisit',
    title: 'Book site visit',
    startsWhen: 'Customer taps "Book site visit" / "Visit today".',
    messages: [
      {
        key: 'ask',
        label: 'Ask for a slot',
        required: [],
        placeholders: ['name', 'project', 'location'],
        defaults: {
          en: 'Great choice, {name}! 🙌 Pick a time to visit *{project}*.',
          gu: 'સરસ, {name}! 🙌 *{project}* ની વિઝિટ માટે સમય પસંદ કરો.',
          hi: 'बढ़िया, {name}! 🙌 *{project}* की विज़िट के लिए समय चुनें।',
        },
        listButton: { id: 'slots', rows: 'slots', defaults: { en: 'Pick a slot', gu: 'સમય પસંદ કરો', hi: 'समय चुनें' } },
      },
      {
        key: 'confirm',
        label: 'Visit confirmed',
        required: ['slot'],
        placeholders: ['name', 'project', 'location', 'slot', 'exec'],
        defaults: {
          en: 'Your visit to *{project}* is booked for {slot} ✅\n📍 {location}\n{exec} will call you to confirm. See you soon!',
          gu: '*{project}* ની તમારી વિઝિટ {slot} માટે બુક થઈ ગઈ છે ✅\n📍 {location}\n{exec} કન્ફર્મ કરવા કૉલ કરશે. જલ્દી મળીએ!',
          hi: '*{project}* की आपकी विज़िट {slot} के लिए बुक हो गई है ✅\n📍 {location}\n{exec} कन्फर्म करने के लिए कॉल करेंगे। जल्द मिलते हैं!',
        },
        sendsLocation: true,
      },
    ],
    action: 'Creates a site-visit follow-up for the assigned executive at the picked slot.',
    end: 'The chat goes to the Messages inbox with the visit card.',
  },
  {
    key: 'callback',
    title: 'Request a call',
    startsWhen: 'Customer taps "Request a call". {callTime} names the next office day, e.g. "Monday morning" on a Saturday night.',
    messages: [
      {
        key: 'ask',
        label: 'Ask for a time',
        required: [],
        placeholders: ['name', 'next'],
        defaults: {
          en: 'Sure, {name}! What time suits you for a call?',
          gu: 'ચોક્કસ, {name}! કૉલ માટે કયો સમય અનુકૂળ છે?',
          hi: 'ज़रूर, {name}! कॉल के लिए कौन सा समय ठीक रहेगा?',
        },
        buttons: [
          { id: 'morning', defaults: { en: 'Morning', gu: 'સવારે', hi: 'सुबह' } },
          { id: 'afternoon', defaults: { en: 'Afternoon', gu: 'બપોરે', hi: 'दोपहर' } },
          { id: 'evening', defaults: { en: 'Evening', gu: 'સાંજે', hi: 'शाम' } },
        ],
      },
      {
        key: 'confirm',
        label: 'Call-back confirmed',
        required: ['callTime'],
        placeholders: ['name', 'project', 'callTime', 'exec'],
        defaults: {
          en: 'Done ✅ {exec} will call you {callTime}. Thank you, {name}!',
          gu: 'થઈ ગયું ✅ {exec} તમને {callTime} કૉલ કરશે. આભાર, {name}!',
          hi: 'हो गया ✅ {exec} आपको {callTime} कॉल करेंगे। धन्यवाद, {name}!',
        },
      },
    ],
    action: 'Creates a call-back follow-up for the assigned executive at the picked time.',
    end: 'The chat goes to the Messages inbox with the call-back card.',
  },
  {
    key: 'projectExplore',
    title: 'Explore projects',
    startsWhen: 'A customer taps Select project or Explore projects in the enquiry welcome, or requests a brochure or visit without a known project.',
    messages: [
      {
        key: 'choose',
        label: 'Choose a project',
        required: [],
        placeholders: ['name'],
        defaults: {
          en: 'Which Swarna Group project would you like to explore? Choose one to see its possession timeline and next options.',
          gu: 'તમે સ્વર્ણ ગ્રુપનો કયો પ્રોજેક્ટ જોવા માંગો છો? પઝેશન સમયરેખા અને આગળના વિકલ્પો જોવા એક પસંદ કરો.',
          hi: 'आप स्वर्ण ग्रुप का कौन सा प्रोजेक्ट देखना चाहेंगे? पज़ेशन की समयसीमा और आगे के विकल्प देखने के लिए एक चुनें।',
        },
        listButton: { id: 'projects', rows: 'projects', defaults: { en: 'Select project', gu: 'પ્રોજેક્ટ પસંદ કરો', hi: 'प्रोजेक्ट चुनें' } },
      },
      {
        key: 'detail',
        label: 'Project options',
        required: ['project'],
        placeholders: ['project', 'possession'],
        defaults: {
          en: '*{project}*\n{possession}\n\nWhat would you like to do next?',
          gu: '*{project}*\n{possession}\n\nહવે તમે શું કરવા માંગો છો?',
          hi: '*{project}*\n{possession}\n\nअब आप क्या करना चाहेंगे?',
        },
        buttons: [BTN_BROCHURE, BTN_VISIT, BTN_CALLBACK],
      },
    ],
    action: 'The project list shows published projects and their recorded possession timelines. A choice records interest without changing an existing lead’s primary project. Brochure and visit requests continue for the selected project.',
  },
  {
    key: 'unknownContact',
    title: 'Unknown contact',
    startsWhen: 'A number that is not in the CRM writes for the first time.',
    messages: [
      {
        key: 'main',
        label: 'Which project?',
        required: [],
        placeholders: ['name', 'next'],
        defaults: {
          en: 'Hi {name}, welcome to *Swarna Group*! 🏡\n\nThank you for reaching out. We have residential and commercial projects in Surat.\n\nWhich project would you like to know about? Tap *View projects* to choose one, and we’ll send you the details right away.\n\nPrefer to talk to a person? Choose *Talk to our team* from the list.',
          gu: 'નમસ્તે {name}, *સ્વર્ણ ગ્રુપ* માં આપનું સ્વાગત છે! 🏡\n\nસંપર્ક કરવા બદલ આભાર. સુરતમાં અમારા રહેણાંક અને કોમર્શિયલ પ્રોજેક્ટ્સ છે.\n\nતમે કયા પ્રોજેક્ટ વિશે જાણવા માંગો છો? *પ્રોજેક્ટ જુઓ* પર ટેપ કરીને પસંદ કરો, અમે તરત જ માહિતી મોકલીશું.\n\nટીમ સાથે સીધી વાત કરવી છે? લિસ્ટમાંથી *ટીમ સાથે વાત કરો* પસંદ કરો.',
          hi: 'नमस्ते {name}, *स्वर्ण ग्रुप* में आपका स्वागत है! 🏡\n\nसंपर्क करने के लिए धन्यवाद। सूरत में हमारे रेज़िडेंशियल और कमर्शियल प्रोजेक्ट्स हैं।\n\nआप किस प्रोजेक्ट के बारे में जानना चाहेंगे? *प्रोजेक्ट देखें* पर टैप करके चुनें, हम तुरंत जानकारी भेजेंगे।\n\nटीम से सीधे बात करनी है? लिस्ट में से *टीम से बात करें* चुनें।',
        },
        listButton: { id: 'projects', rows: 'projects', defaults: { en: 'Select project', gu: 'પ્રોજેક્ટ પસંદ કરો', hi: 'प्रोजेक्ट चुनें' } },
        fixedRows: [{ id: 'human', defaults: { en: '💬 Talk to our team', gu: '💬 ટીમ સાથે વાત કરો', hi: '💬 टीम से बात करें' } }],
      },
    ],
    action: 'A project pick tags the contact and sends the after-hours or busy reply for the current time. "Talk to our team" (always the last row) flags the chat for the next executive. The list shows up to 9 projects.',
    end: 'A new lead is created with source WhatsApp.',
  },
  {
    key: 'existingCustomer',
    title: 'Existing customer',
    startsWhen: 'A booked customer writes outside office hours. No sales buttons are sent.',
    messages: [
      {
        key: 'main',
        label: 'Customer care reply',
        required: ['next'],
        placeholders: ['name', 'project', 'next', 'exec'],
        defaults: {
          en: 'Hi {name} 🙏\n\nThank you for your message. As part of the *Swarna Group* family, your query matters to us.\n\nOur office is closed right now. Our customer care team will get back to you by *{next}*.\n\nFor anything urgent, you can call *{exec}* directly.',
          gu: 'નમસ્તે {name} 🙏\n\nતમારા મેસેજ બદલ આભાર. તમે *સ્વર્ણ ગ્રુપ* પરિવારનો ભાગ છો, તમારી દરેક વાત અમારા માટે મહત્વની છે.\n\nઅત્યારે અમારી ઓફિસ બંધ છે. અમારી કસ્ટમર કેર ટીમ *{next}* સુધીમાં તમારો સંપર્ક કરશે.\n\nકંઈ તાત્કાલિક હોય તો *{exec}* ને સીધો કૉલ કરી શકો છો.',
          hi: 'नमस्ते {name} 🙏\n\nआपके मैसेज के लिए धन्यवाद। आप *स्वर्ण ग्रुप* परिवार का हिस्सा हैं, आपकी हर बात हमारे लिए ज़रूरी है।\n\nअभी हमारा ऑफिस बंद है। हमारी कस्टमर केयर टीम *{next}* तक आपसे संपर्क करेगी।\n\nकुछ ज़रूरी हो तो आप *{exec}* को सीधे कॉल कर सकते हैं।',
        },
      },
    ],
    action: 'Saves a "customer care" record. The chat stays unread for the team.',
    end: 'The chat goes to the Messages inbox for the team.',
  },
  {
    key: 'optOut',
    title: 'Opt-out',
    startsWhen: 'Customer sends STOP, UNSUBSCRIBE, "stop messages", બંધ or बंद. Sending START turns automatic messages back on.',
    messages: [
      {
        key: 'main',
        label: 'Opt-out confirmation',
        required: [],
        placeholders: ['name'],
        defaults: {
          en: "✅ *Done, {name}.*\n\nYou won't get automatic messages from *Swarna Group* any more.\n\n▪️ To turn them back on, reply *START*\n▪️ Need help? Just write to us, our team will reply personally.\n\n_Thank you_ 🙏",
          gu: '✅ *થઈ ગયું, {name}.*\n\nહવે તમને *સ્વર્ણ ગ્રુપ* તરફથી ઓટોમેટિક મેસેજ નહીં મળે.\n\n▪️ ફરી ચાલુ કરવા *START* લખો\n▪️ કોઈ મદદ જોઈએ? અમને લખો, અમારી ટીમ જાતે જવાબ આપશે.\n\n_આભાર_ 🙏',
          hi: '✅ *हो गया, {name}.*\n\nअब आपको *स्वर्ण ग्रुप* से ऑटोमैटिक मैसेज नहीं मिलेंगे।\n\n▪️ फिर से चालू करने के लिए *START* लिखें\n▪️ कोई मदद चाहिए? हमें लिखें, हमारी टीम खुद जवाब देगी।\n\n_धन्यवाद_ 🙏',
        },
      },
      {
        key: 'optIn',
        label: 'Turned back on (customer sent START)',
        required: [],
        placeholders: ['name'],
        defaults: {
          en: '🔔 *Welcome back, {name}!*\n\nUpdates from *Swarna Group* are on again: visit reminders, project news and replies to your questions.\n\nReply *STOP* anytime to turn them off.',
          gu: '🔔 *ફરી સ્વાગત છે, {name}!*\n\n*સ્વર્ણ ગ્રુપ* તરફથી અપડેટ ફરી ચાલુ થઈ ગયા છે: વિઝિટ રિમાઇન્ડર, પ્રોજેક્ટ સમાચાર અને તમારા પ્રશ્નોના જવાબ.\n\nબંધ કરવા ગમે ત્યારે *STOP* લખો.',
          hi: '🔔 *फिर से स्वागत है, {name}!*\n\n*स्वर्ण ग्रुप* से अपडेट फिर से चालू हो गए हैं: विज़िट रिमाइंडर, प्रोजेक्ट की खबरें और आपके सवालों के जवाब।\n\nबंद करने के लिए कभी भी *STOP* लिखें।',
        },
      },
    ],
    action: 'Marks the contact as opted out: no auto-replies, campaigns or reminders until they send START. The team can still reply by hand.',
    end: 'The chat goes to the Messages inbox for the team.',
  },
  {
    key: 'shared',
    title: 'Shared footer',
    startsWhen: 'Shown in small grey text under every message that has buttons or a list. WhatsApp shows no footer on plain text, documents without buttons or location pins.',
    messages: [
      {
        key: 'footer',
        label: 'Footer',
        role: 'footer',
        required: [],
        placeholders: [],
        defaults: {
          en: 'Swarna Group · Reply STOP to opt out',
          gu: 'સ્વર્ણ ગ્રુપ · બંધ કરવા STOP લખો',
          hi: 'स्वर्ण ग्रुप · बंद करने के लिए STOP लिखें',
        },
      },
    ],
  },
];

/** Stored content: flat keys `${flow}.${message}` and `${flow}.${message}.${button}`. */
export interface AutoReplyContent {
  texts: Record<string, LangText>;
  buttons: Record<string, LangText>;
  triggerPhrases: Record<string, string[]>;
}

export const textKey = (flow: string, message: string) => `${flow}.${message}`;
export const buttonKey = (flow: string, message: string, button: string) => `${flow}.${message}.${button}`;

export const messageButtons = (msg: FlowMessageDef): FlowButtonDef[] =>
  [...(msg.buttons || []), ...(msg.listButton ? [msg.listButton] : []), ...(msg.fixedRows || [])];

/** Character limit for a button / list button / list row label. */
export const labelMaxChars = (msg: FlowMessageDef, id: string) =>
  msg.fixedRows?.some((row) => row.id === id) ? ROW_MAX_CHARS : BUTTON_MAX_CHARS;

export const messageMaxChars = (msg: FlowMessageDef) => (msg.role === 'footer' ? FOOTER_MAX_CHARS : MESSAGE_MAX_CHARS);

export const FOOTER_KEY = 'shared.footer';

export const builtInContent = (): AutoReplyContent => {
  const content: AutoReplyContent = { texts: {}, buttons: {}, triggerPhrases: {} };
  TEXT_TRIGGER_FLOWS.forEach((key) => { content.triggerPhrases[key] = []; });
  AUTO_REPLY_FLOWS.forEach((flow) => {
    flow.messages.forEach((msg) => {
      content.texts[textKey(flow.key, msg.key)] = { ...msg.defaults };
      messageButtons(msg).forEach((btn) => {
        content.buttons[buttonKey(flow.key, msg.key, btn.id)] = { ...btn.defaults };
      });
    });
  });
  return content;
};

/** Fills anything missing in `raw` from `fallback`, and drops keys no flow uses. */
export const mergeContent = (raw: any, fallback: AutoReplyContent): AutoReplyContent => {
  const pick = (source: any, base: LangText): LangText => ({
    en: typeof source?.en === 'string' ? source.en : base.en,
    gu: typeof source?.gu === 'string' ? source.gu : base.gu,
    hi: typeof source?.hi === 'string' ? source.hi : base.hi,
  });
  const content: AutoReplyContent = { texts: {}, buttons: {}, triggerPhrases: {} };
  Object.entries(fallback.texts).forEach(([key, base]) => {
    content.texts[key] = pick(raw?.texts?.[key], base);
  });
  Object.entries(fallback.buttons).forEach(([key, base]) => {
    content.buttons[key] = pick(raw?.buttons?.[key], base);
  });
  TEXT_TRIGGER_FLOWS.forEach((key) => {
    content.triggerPhrases[key] = Array.isArray(raw?.triggerPhrases?.[key]) ?
      raw.triggerPhrases[key].filter((value: unknown): value is string => typeof value === 'string') :
      raw == null ? [...(fallback.triggerPhrases[key] || [])] : [];
  });
  return content;
};

export const contentEquals = (a: AutoReplyContent | null, b: AutoReplyContent | null) =>
  JSON.stringify(a) === JSON.stringify(b);

const TOKEN_RE = /\{(\w+)\}/g;

export const tokensIn = (text: string) => Array.from(new Set(Array.from(String(text || '').matchAll(TOKEN_RE)).map((m) => m[1])));

export interface ContentIssue {
  flowKey: string;
  messageKey: string;
  lang?: AutoReplyLang;
  text: string;
}

export const validateContent = (content: AutoReplyContent): ContentIssue[] => {
  const issues: ContentIssue[] = [];
  const known = new Set(PLACEHOLDERS.map((p) => p.key));
  const seenPhrases = new Map<string, string>();
  TEXT_TRIGGER_FLOWS.forEach((flowKey) => {
    const phrases = content.triggerPhrases?.[flowKey] || [];
    const flow = AUTO_REPLY_FLOWS.find((item) => item.key === flowKey)!;
    const messageKey = flow.messages[0].key;
    if (phrases.length > TRIGGER_PHRASES_MAX) {
      issues.push({ flowKey, messageKey, text: `${flow.title}: use at most ${TRIGGER_PHRASES_MAX} trigger phrases.` });
    }
    phrases.forEach((phrase) => {
      const normalized = phrase.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
      if (!normalized) return;
      if (phrase.length > TRIGGER_PHRASE_MAX_CHARS) {
        issues.push({ flowKey, messageKey, text: `${flow.title}: trigger phrases must be ${TRIGGER_PHRASE_MAX_CHARS} characters or less.` });
      }
      if ([...OPT_OUT_KEYWORDS, ...OPT_IN_KEYWORDS].some((keyword) => keyword.toLocaleLowerCase() === normalized)) {
        issues.push({ flowKey, messageKey, text: `${flow.title}: “${phrase}” is reserved for WhatsApp opt-in or opt-out.` });
      }
      const owner = seenPhrases.get(normalized);
      if (owner) issues.push({ flowKey, messageKey, text: `“${phrase}” is already a trigger in ${owner}.` });
      else seenPhrases.set(normalized, flow.title);
    });
  });
  AUTO_REPLY_FLOWS.forEach((flow) => {
    flow.messages.forEach((msg) => {
      const texts = content.texts[textKey(flow.key, msg.key)];
      AUTO_REPLY_LANGS.forEach(({ key: lang, label }) => {
        const text = String(texts?.[lang] || '');
        const where = `${flow.title} → ${msg.label} (${label})`;
        const max = messageMaxChars(msg);
        if (!text.trim()) {
          if (msg.role !== 'footer') issues.push({ flowKey: flow.key, messageKey: msg.key, lang, text: `${where}: message is empty.` });
          return;
        }
        if (text.length > max) {
          issues.push({ flowKey: flow.key, messageKey: msg.key, lang, text: `${where}: longer than ${max} characters.` });
        }
        msg.required.forEach((token) => {
          if (!text.includes(`{${token}}`)) {
            issues.push({ flowKey: flow.key, messageKey: msg.key, lang, text: `${where}: must keep {${token}}.` });
          }
        });
        tokensIn(text)
          .filter((token) => !known.has(token))
          .forEach((token) => {
            issues.push({ flowKey: flow.key, messageKey: msg.key, lang, text: `${where}: unknown placeholder {${token}}.` });
          });
      });
      AUTO_REPLY_LANGS.forEach(({ key: lang, label }) => {
        const titles = (msg.buttons || []).map((btn) => String(content.buttons[buttonKey(flow.key, msg.key, btn.id)]?.[lang] || '').trim());
        if (new Set(titles).size !== titles.length) {
          issues.push({ flowKey: flow.key, messageKey: msg.key, lang, text: `${flow.title} → ${msg.label} (${label}): button labels must be different.` });
        }
      });
      messageButtons(msg).forEach((btn) => {
        const labels = content.buttons[buttonKey(flow.key, msg.key, btn.id)];
        AUTO_REPLY_LANGS.forEach(({ key: lang, label }) => {
          const value = String(labels?.[lang] || '').trim();
          const where = `${flow.title} → button "${btn.defaults.en}" (${label})`;
          const max = labelMaxChars(msg, btn.id);
          if (!value) issues.push({ flowKey: flow.key, messageKey: msg.key, lang, text: `${where}: label is empty.` });
          else if (value.length > max) {
            issues.push({ flowKey: flow.key, messageKey: msg.key, lang, text: `${where}: longer than ${max} characters.` });
          }
        });
      });
    });
  });
  return issues;
};

const formatTime12 = (hhmm: string) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
};

const toMinutes = (hhmm: string) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return h * 60 + m;
};

/** Office open/close for a calendar day, after applying holidays. */
const officeWindowFor = (settings: WhatsappAutoReplySettings, ymd: string, dayKey: string) => {
  const holiday = settings.holidays.find((h) => ymd >= h.fromDate && ymd <= h.toDate);
  if (holiday) {
    return holiday.mode === 'custom' && holiday.from && holiday.to ? { from: holiday.from, to: holiday.to } : null;
  }
  const day = settings.weekly[dayKey as keyof WhatsappAutoReplySettings['weekly']];
  return day && day.mode === 'office' ? { from: day.from, to: day.to } : null;
};

/** "10:00 AM tomorrow", "Monday 10:00 AM", "today 10:00 AM"; null when nothing opens in the next 2 weeks. */
export const nextOfficeOpening = (settings: WhatsappAutoReplySettings, now: Date): string | null => {
  const today = zonedParts(now, settings.timezone);
  for (let offset = 0; offset < 15; offset += 1) {
    const day = zonedParts(new Date(now.getTime() + offset * 86400000), settings.timezone);
    const window = officeWindowFor(settings, day.ymd, day.dayKey);
    if (!window) continue;
    if (offset === 0 && toMinutes(window.from) <= today.minutes) continue;
    const time = formatTime12(window.from);
    if (offset === 0) return `today ${time}`;
    if (offset === 1) return `${time} tomorrow`;
    const weekday = new Date(`${day.ymd}T00:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'long' });
    return `${weekday} ${time}`;
  }
  return null;
};

/** "today" / "tomorrow" / "Monday": the next day the office opens (from now on). */
export const nextOfficeDayLabel = (settings: WhatsappAutoReplySettings, now: Date): string | null => {
  const today = zonedParts(now, settings.timezone);
  for (let offset = 0; offset < 15; offset += 1) {
    const day = zonedParts(new Date(now.getTime() + offset * 86400000), settings.timezone);
    const window = officeWindowFor(settings, day.ymd, day.dayKey);
    if (!window) continue;
    if (offset === 0 && toMinutes(window.to) <= today.minutes) continue;
    if (offset === 0) return 'today';
    if (offset === 1) return 'tomorrow';
    return new Date(`${day.ymd}T00:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'long' });
  }
  return null;
};

/** Site coordinates from the CMS fields, else parsed from a Google Maps embed / share URL. */
export const projectCoordinates = (project: CmsProject | null | undefined): { latitude: number; longitude: number } | null => {
  if (!project) return null;
  const lat = Number(project.latitude);
  const lng = Number(project.longitude);
  if (project.latitude !== null && project.latitude !== undefined && project.latitude !== '' && Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
    return { latitude: lat, longitude: lng };
  }
  const url = String(project.mapEmbedUrl || '');
  const embed = url.match(/!3d(-?\d+(?:\.\d+)?)!2d(-?\d+(?:\.\d+)?)/) || url.match(/!2d(-?\d+(?:\.\d+)?)!3d(-?\d+(?:\.\d+)?)/);
  if (embed) {
    const [a, b] = [Number(embed[1]), Number(embed[2])];
    return url.indexOf('!3d') < url.indexOf('!2d') ? { latitude: a, longitude: b } : { latitude: b, longitude: a };
  }
  const plain = url.match(/[?&@](?:q=|ll=)?(-?\d{1,2}\.\d+),\s*(-?\d{1,3}\.\d+)/);
  return plain ? { latitude: Number(plain[1]), longitude: Number(plain[2]) } : null;
};

const configLabel = (project: CmsProject | null) => {
  const types = (project?.configurations || []).map((c) => String(c.type || '').trim()).filter(Boolean);
  if (!types.length) return '';
  const bhk = types.filter((t) => /bhk/i.test(t)).map((t) => t.replace(/\s*bhk.*/i, '').trim());
  const others = types.filter((t) => !/bhk/i.test(t));
  const parts: string[] = [];
  if (bhk.length) parts.push(`${bhk.join(' & ')} BHK`);
  return [...parts, ...others].join(', ');
};

const priceLabel = (project: CmsProject | null) => {
  const first = (project?.configurations || []).map((c) => String(c.price || '').trim()).find(Boolean);
  if (!first) return '';
  return `from ${first.split(/[–-]/)[0].trim()}`;
};

const possessionLabel = (project: CmsProject | null) => {
  if (!project) return '';
  if (project.status === 'ready-possession') return 'Ready possession';
  return project.completionDate ? `Possession: ${project.completionDate}` : '';
};

export interface RenderContext {
  name?: string;
  project?: CmsProject | null;
  exec?: string;
  slot?: string;
  callTime?: string;
  settings: WhatsappAutoReplySettings;
  now: Date;
}

export const placeholderValues = (ctx: RenderContext): Record<string, string> => ({
  name: ctx.name?.trim() || 'there',
  project: ctx.project?.name || 'our projects',
  location: ctx.project?.location || '',
  config: configLabel(ctx.project || null),
  price: priceLabel(ctx.project || null),
  possession: possessionLabel(ctx.project || null),
  rera: ctx.project?.reraNumber || '',
  next: nextOfficeOpening(ctx.settings, ctx.now) || 'the next working day',
  exec: ctx.exec?.trim() || 'our team',
  slot: ctx.slot || '',
  callTime: ctx.callTime || `${nextOfficeDayLabel(ctx.settings, ctx.now) || 'tomorrow'} morning`,
});

/** Fills placeholders; a line whose placeholders all came out empty is dropped. */
export const renderTemplate = (text: string, values: Record<string, string>) => {
  const out: string[] = [];
  String(text || '').split('\n').forEach((line) => {
    const tokens = Array.from(line.matchAll(TOKEN_RE)).map((m) => m[1]).filter((key) => key in values);
    if (tokens.length && tokens.every((key) => !String(values[key] || '').trim())) return;
    const filled = line.replace(TOKEN_RE, (match, key) => (key in values ? values[key] : match));
    out.push(filled.replace(/\s*·\s*$/, '').replace(/^\s*·\s*/, ''));
  });
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};
